import { Button, Input, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useCallback, useState, useRef, useEffect } from "react";
import { useDispatch, useSelector, useStore as useReduxStore } from "react-redux";
import { useStore, getSmoothStepPath, EdgeLabelRenderer } from "reactflow";
import { getAutomaticDisplayType } from "./util";
import { fotActions } from "../../store/fot";
import { editorActions } from "../../store/editor";
import { studioActions } from "../../store/studio";
import func from "@uikit/func";
import edgeStyle from "./css/CustomEdge.module.css";
import usePostUrlCanAbort from "../../custom-hooks/usePostUrlCanAbort";
import useOpenAiParam from "../../custom-hooks/useOpenAiParam";
import useFeatureTags from "../../custom-hooks/useFeatureTags";
import { CreatorStoreMethods } from "@uikit/service/CreatorStoreMethods";

import {
  AI_INPUT_PLACEHOLDER,
  GROUPID_REG,
  FUNCTION_REG,
  ENABLE_SPLIT_TYPES,
  IMAGE_GEN_SELECT_LIST,
  INPUT_DEFAULT_WIDTH,
  INPUT_MAX_WIDTH,
} from "./constants";
import { getMidjourney } from "./nodeTypeComponents/nodeTypeDispose";
import useHandleChatBox from "../../custom-hooks/useHandleChatBox";
import useShowStockInterfaceFunc from "../../custom-hooks/useShowStockInterfaceFunc";
import SourceOfTruth from "../components/SourceOfTruth";
import useConnectAgentNode from "@uiview/views/Nodes/Agent/useConnectAgentNode";
import skipEmptyVerification from "@uiview/views/Nodes/Agent/skipEmptyVerification";
import { logEvent } from "@uikit/service/amplitude";
import useCustomEdgeDisabled from "../../custom-hooks/useCustomEdgeDisabled";
import { TooltipWhatTodo } from "../components/TooltipWhatTodo";
import { get, isEmpty } from "lodash";
import useIntroTooltip from "../../custom-hooks/useIntroTooltip";
import utilIndex from "./utils";
import { useFuzzySearch } from "../../uikit/hooks/useFuzzySearch";
import { useHomeReactFlow } from "@custom-hooks/HomeUsePluginRoot/useHomeReactFlow";
import { uploadTypes } from "../../uiview/views/Nodes/constants";
import { CustomEdgeUtil } from "@uikit/util/CustomEdgeUtil";
import { CustomEedgProvider, useCustomEdgeContainer } from "@uiview/views/CustomNewEdge/container";
import { CommandBar } from "@uiview/views/CommandBar";
import { useNavigate } from "react-router-dom";
import { creatorRefStore } from "@uiview/views/ProjectCanvas/store/CreatorRefStore";
import { getIt } from "@uikit/getIt";
import { CreatorNodesStore } from "@uiview/views/ProjectCanvas/store/CreatorNodesStore";
import { CreatorEdgesStore } from "@uiview/views/ProjectCanvas/store/CreatorEdgesStore";
import { HomeMethodsUtil } from "@uikit/util/_homeUtil";
import { useHomePlugin } from "./HomeUsePluginV2";
import { useHomeHandleFieldMethods } from "@uiview/views/ProjectCanvas/useHomeHandleFieldMethods";
import useSetNodeErrorTips from "@custom-hooks/useSetNodeErrorTips";
import { useHomeServices } from "@uiview/views/ProjectCanvas/useHomeServices";
import useHandleOtherTypeRun from "@custom-hooks/useHandleOtherTypeRun";
import { useHomeChildContainer } from "@custom-hooks/HomeUsePluginRoot/childContainer";

const { TextArea } = Input;
const distance = 30;
const selectedEdgeStyl = {
  strokeWidth: 4,
  stroke: "#e84e4e",
};
const style = {
  strokeWidth: 2,
  stroke: "#6299f9",
};

const disableAutoRunList = ["SourceOfTruth"];
const pathVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      delay: 0.2,
      ease: "easeInOut",
    },
  },
};

function CustomEdgeInner(props) {
  const creatorNodesStore = getIt(CreatorNodesStore);
  const creatorEdgesStore = getIt(CreatorEdgesStore);

  const { id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd } =
    props;
  const store = useReduxStore();
  const dispatch = useDispatch();
  const feature_tags = useFeatureTags();
  const navigate = useNavigate();
  const { setNodeErrorTip } = useSetNodeErrorTips();
  const { setChatBoxParam } = useHandleChatBox();
  const { setConnectAgentNode } = useConnectAgentNode();
  const homePlugin = useHomePlugin();
  const homeServices = useHomeServices();
  const creatorStoreMethods = getIt(CreatorStoreMethods);
  const { handleStockInterfaceFunc } = useShowStockInterfaceFunc({
    postAIDescription: homePlugin.postAIDescription,
    setNodeParam: creatorStoreMethods.setNodeParam,
  });

  const homeHandleFieldMethods = useHomeHandleFieldMethods({});

  const homeChildContainer = useHomeChildContainer();

  const { handleUploadFileRun } = useHandleOtherTypeRun({
    getHomeNodes: creatorStoreMethods.getHomeNodes,
    getSourceOfTruthResults: homeServices.getSourceOfTruthResults,
    setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
  });

  const { deleteNodeById, setCenter } = useHomeReactFlow();
  const {
    showImageGenSelect,
    disableHover,
    setDisableHover,
    searchFunc,
    setSearchFunc,
    aiDescription,
    setAiDescription,
    aiDescriptionInputRef,
    edgeState,
    apiArr,
    setApiArr,
    onChangeVisibleCommandBar: setShowImageGenSelect,
    focusEdge,
  } = useCustomEdgeContainer();

  const [aiDescriptionWidth, setAiDescriptionWidth] = useState(INPUT_DEFAULT_WIDTH);
  const [selectValue, setSelectValue] = useState("stockInfo");
  // const [apiArr, setApiArr] = useState([]);

  const prevAiDescription = useRef("");
  const aiDescriptionSpanRef = useRef(null);
  const prevNodeContent = useRef([]);
  const prevEdgeData = useRef({});

  const sharedProps = {
    ref: aiDescriptionInputRef,
  };

  const { onFocusToFocusEdge, setEdgeState } = focusEdge;

  const sourceNode = useStore(useCallback(store => store.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback(store => store.nodeInternals.get(target), [target]));

  const developerMode = useSelector(state => state.fot.developerMode);

  const currentModalLineParamStr = useSelector(state => state.editor.currentModalLineParamStr);

  const lineParam = useSelector(state => state.fot.lineParam);
  const setLineParam = val => {
    dispatch(fotActions.setLineParam(val));
  };

  const isModelOpen = useSelector(state => state.fot.isModelOpen);
  const setIsModelOpen = val => {
    dispatch(fotActions.setIsModelOpen(val));
  };

  const commandEnterNodeId = useSelector(state => state.fot.commandEnterNodeId);
  const setCommandEnterNodeId = val => {
    dispatch(fotActions.setCommandEnterNodeId(val));
  };

  const checkEdgeArr = useSelector(state => state.fot.checkEdgeArr);

  const selectedTemplate = useSelector(state => state.studio.selectedTemplate);
  const setSelectedTemplate = val => {
    dispatch(studioActions.setSelectedTemplate(val));
  };

  const createFunctionClick = useSelector(state => state.fot.createFunctionClick);
  const setCreateFunctionClick = val => {
    dispatch(fotActions.setCreateFunctionClick(val));
  };
  const imageGenSelectOptions = useSelector(state => state.fot.imageGenSelectOptions);
  const setImageGenSelectOptions = val => {
    dispatch(fotActions.setImageGenSelectOptions(val));
  };

  const selectIdArr = useSelector(state => state.fot.selectIdArr);
  const sameTargetEdgeIds = useSelector(state => state.editor.sameTargetEdgeIds);
  const setCheckNodeArr = val => {
    dispatch(fotActions.setCheckNodeArr(val));
  };
  const setCreateJSFuncData = val => {
    dispatch(editorActions.setCreateJSFuncData(val));
  };
  const setCreateAPIFuncData = val => {
    dispatch(editorActions.setCreateAPIFuncData(val));
  };
  const setShowTooltip = val => {
    dispatch(editorActions.setShowTooltip(val));
  };
  const setCloseToolTipArr = val => {
    dispatch(editorActions.setCloseToolTipArr(val));
  };
  const getShowTooltip = useSelector(state => get(state, "editor.showTooltip", ""));

  const needDisableEdgeIds = useSelector(state => state.editor.needDisableEdgeIds);
  const setNeedDisableEdgeIds = val => {
    dispatch(editorActions.setNeedDisableEdgeIds(val));
  };

  const { postData, postWithStreaming } = usePostUrlCanAbort();
  const { getOpenAiParam, getCompletionsGenerationParam } = useOpenAiParam();

  const { introTooltipObj } = useIntroTooltip(id, null, edgeState, sourcePosition);
  // const updateNodeInternals = useUpdateNodeInternals();

  const isUploadType = uploadTypes.includes(sourceNode?.data?.displayType);

  useEffect(() => {
    if (!func.isEmpty(createFunctionClick) && createFunctionClick.edgeId === id) {
      requestSavedFunction(createFunctionClick.functionName);
    }
  }, [createFunctionClick]);

  // 监听command enter后执行run
  useEffect(() => {
    // generate/summary ai description适配
    if (commandEnterNodeId instanceof Object && !func.isEmpty(commandEnterNodeId)) {
      if (commandEnterNodeId.nodeId !== source || commandEnterNodeId.targetNodeId !== target) return;
    } else if (commandEnterNodeId !== source) {
      return;
    }
    // if (commandEnterNodeId !== source) return;
    clickRunBtn(commandEnterNodeId?.param || {}).then(() => {
      setCommandEnterNodeId("");
    });
  }, [commandEnterNodeId]);

  // useEffect(() => {
  //   if (!data.isSliceAutoRun) return;
  //   clickToSelectValue(data.selectedApi)
  // }, [data.isSliceAutoRun])

  //应该初始化的时候执行，解决切换project，没有执行，暂时监听data
  useEffect(() => {
    if (!data.isSliceAutoRun) {
      if (!data.isFromSaveData && !showImageGenSelect && !data.isUndoRedoAdd) {
        setCheckNodeArr([]);
        aiDescriptionInputRef.current?.focus();
      }
      creatorEdgesStore.setQueryLoading(id, "isUndoRedoAdd", false);
      const enterText = data?.lineParam?.enterText || "";
      if (enterText && enterText !== prevAiDescription.current) {
        setAiDescription(enterText);
      }
      const aiStr = enterText.replace(FUNCTION_REG, "");
      const arr = [...IMAGE_GEN_SELECT_LIST, ...store.getState().fot.imageGenSelectOptions];
      const select = arr.find(x => x.label === aiStr);
      const selectVal = select?.value || "stockInfo";
      setSelectValue(selectVal);
    }

    // FIXME: 监听 length 不够严谨
    //  bsf-2424 cusotmDropdown 会设置 imageGenSelectOptions，另外的边会监听，改变监听为长度
  }, [data.isSliceAutoRun, data.isFromSaveData, data?.lineParam, imageGenSelectOptions.length]);

  const { disabledRun } = useCustomEdgeDisabled({ id, data, sourceNode });

  // 如果是create api function
  useEffect(() => {
    if (!data.isApiFunction) return;
    setShowImageGenSelect(false);
  }, [data.isApiFunction]);

  const resetAiDescriptionWidth = () => {
    if (func.isEmpty(aiDescriptionSpanRef.current)) return;
    const mockSpanWidth = aiDescriptionSpanRef.current.clientWidth;
    if (mockSpanWidth > INPUT_MAX_WIDTH) {
      setAiDescriptionWidth(mockSpanWidth);
      return;
    }
    if (mockSpanWidth + 30 > INPUT_DEFAULT_WIDTH) {
      setAiDescriptionWidth(mockSpanWidth + 30);
      return;
    }
    if (func.isEmpty(aiDescription)) {
      setAiDescriptionWidth(INPUT_DEFAULT_WIDTH);
    }
  };

  useEffect(() => {
    resetAiDescriptionWidth();
  }, [aiDescription]);

  const fuzzyData = {
    filterFarget: store.getState().fot.imageGenSelectOptions,
    options: {
      includeScore: true,
      useExtendedSearch: true,
      threshold: 0.3,
      minMatchCharLength: 3,
      keys: ["label"],
    },
  };
  useFuzzySearch(isUploadType, aiDescription, fuzzyData, filterCompletedArray => {
    if (!func.isEmpty(filterCompletedArray)) {
      setDisableHover(true);
      setApiArr(filterCompletedArray);
      setShowImageGenSelect(true);
    } else {
      setApiArr([]);
      setShowImageGenSelect(false);
    }
  });

  const hasOptionInLists = value => {
    const list = store.getState().fot.imageGenSelectOptions;
    return list.findIndex(x => x.value === value);
  };
  useEffect(() => {
    // create func受限于develop mode
    // setCreateFunctions(prevList => {
    //   return prevList.map(x => {
    //     return {
    //       ...x,
    //       display: developerMode
    //     }
    //   })
    // })

    const idx = hasOptionInLists("midjourney");
    if (!developerMode) {
      // setShowImageGenSelect(false)
      setIsModelOpen(false);
      // creatorEdgesStore.setQueryLoading(id, 'isGetQuery', true)
      // 删除midjourney
      if (idx !== -1) {
        let tempList = [...imageGenSelectOptions];
        tempList.splice(idx, 1);
        setImageGenSelectOptions(tempList);
      }
    } else {
      // 添加midjourney
      if (idx === -1) {
        let tempList = [...imageGenSelectOptions];
        const imageGenIdx = hasOptionInLists("imageGen");
        tempList.splice(imageGenIdx, 0, getMidjourney);
        setImageGenSelectOptions(tempList);
      }
    }
    if (showImageGenSelect) {
      const apiArrs = getFilterSelectOptions(searchFunc);
      setApiArr(apiArrs);
    }
  }, [developerMode]);

  // useEffect(() => {
  //   if (!showImageGenSelect && !data.isUndoRedoAdd) {
  //     aiDescriptionInputRef.current?.focus()
  //     return;
  //   }
  //   // 选中光标后默认 hover 效果
  //   if(aiDescription.includes('/')) {
  //     setDisableHover(false)
  //   }
  // }, [showImageGenSelect])

  useEffect(() => {
    if (creatorRefStore.isNewEdgeRunRef.current) return;
    const currentModalLineParam = currentModalLineParamStr && JSON.parse(currentModalLineParamStr);
    if (currentModalLineParam.id !== id) return;

    const prevLineParamStr = JSON.stringify(prevEdgeData.current.lineParam || {});
    if (prevLineParamStr !== currentModalLineParamStr && !data.isUndoRedoAdd) {
      homeChildContainer.onEdgeContentChange([
        {
          id,
          startEdgeData: {
            isGetQuery: prevEdgeData.current?.isGetQuery || false,
            lineParamStr: prevLineParamStr,
          },
          endEdgeData: {
            isGetQuery: true,
            lineParamStr: currentModalLineParamStr,
          },
        },
      ]);
    }
    creatorEdgesStore.setQueryLoading(id, "isUndoRedoAdd", false);
  }, [currentModalLineParamStr]);

  useEffect(() => {
    if (!data.isGetQuery) {
      if (!data.getQueryLoading) {
        setEdgeState("input");
      } else {
        setEdgeState("generating");
      }
    } else {
      setEdgeState("run");
    }
  }, [data.isGetQuery, data.getQueryLoading]);

  if (!sourceNode || !targetNode) {
    return null;
  }

  // const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);
  // const [edgePath, labelX, labelY] = getSmoothStepPath({
  //   sourceX: sx,
  //   sourceY: sy,
  //   targetX: tx,
  //   targetY: ty,
  //   sourcePosition: sourcePos,
  //   targetPosition: targetPos,
  // });
  const [edgePath, labelX, labelY, offsetX, offsetY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  // 点击单条处理
  function disableNextEdges() {
    const needDisableEdgeArrIds = [];
    getAllNextEdges(needDisableEdgeArrIds, [target]);
    setNeedDisableEdgeIds([...needDisableEdgeIds, ...needDisableEdgeArrIds]);
    return needDisableEdgeArrIds;
  }

  function grouNextNodeId(groupArr) {
    const nodes = creatorNodesStore.getNodes();
    const edges = creatorEdgesStore.getEdges();

    // 获取分组下所有子node
    const childNodeIdArr = nodes.filter(x => groupArr.includes(x.parentNode)).map(x => x.id);
    // 通过子node, 找到起点 source
    const childEdges = edges
      .filter(x => childNodeIdArr.some(y => y === x.source) && !x?.data?.lineParam?.loading)
      .map(x => x.source);
    return childEdges;
  }

  function getAllNextEdges(needDisableEdgeIds, targets) {
    const edges = creatorEdgesStore.getEdges();

    const nextEdges = edges.filter(x => targets.some(y => y === x.source) && !x?.data?.lineParam?.loading);

    const nextEdgeIds = nextEdges.map(x => x.id);
    let nextEdgeTargets = nextEdges.map(x => x.target);

    // 处理分组
    const groupArr = targets.filter(x => GROUPID_REG.test(x));
    if (!func.isEmpty(groupArr)) {
      const childEdges = grouNextNodeId(groupArr);
      nextEdgeTargets = nextEdgeTargets.concat(childEdges);
    }

    needDisableEdgeIds.push(...nextEdgeIds);

    if (!isEmpty(nextEdgeIds) || !func.isEmpty(groupArr)) {
      getAllNextEdges(needDisableEdgeIds, nextEdgeTargets);
    }
  }
  const getCustomDropdownStyl = () => {
    // 是上下方向连接时
    if (sourcePosition === "top" || sourcePosition === "bottom") {
      return {
        transform: `translate(calc(50% + 100px), -50%)`,
      };
    }
    // 不是左->右方向连接时
    if (sourceX > targetX) {
      return {
        transform: "translateY(-50%)",
        left: "150%",
      };
    }
    // 左->右方向连接时
    return {
      transform: `translate(calc(50% + ${offsetX + 10}px), -50%)`,
    };
  };
  const getRenderBoxStyl = () => {
    /*
      只针对input node右方向的连线，将输入框/run按钮往output node方向挪移，input node其他方向的输入框/run按钮位置不变
      单个输入时边内容居中显示
    */
    const multiSameTargetEdgeIds = store.getState().editor.multiSameTargetEdgeIds;
    if (data.sourceHandle !== "b" || !multiSameTargetEdgeIds.includes(id)) {
      return {
        width: `${targetX - sourceX}px`,
        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
      };
    }
    // 横轴正向
    const isXPositiveDirection = targetX - sourceX > 0;
    // 纵轴正向
    const isYPositiveDirection = targetY - sourceY > 0;
    let mvX = labelX;
    let mvY = labelY;
    // 左
    if (isXPositiveDirection) {
      mvX = labelX + offsetX / 2;
      mvY = isYPositiveDirection ? labelY + offsetY : labelY - offsetY;
    }

    return {
      width: `${targetX - sourceX}px`,
      transform: `translate(-50%, -50%) translate(${mvX}px,${mvY}px)`,
    };
  };
  const getOverlayStyl = () => {
    const multiSameTargetEdgeIds = store.getState().editor.multiSameTargetEdgeIds;
    // 不是从右边连线时
    if (data.sourceHandle !== "b") {
      return {};
    }
    // 是单条边入口时
    if (!multiSameTargetEdgeIds.includes(id)) {
      if (edgeState !== "input") {
        return {
          transform: `translateX(${offsetX}px)`,
        };
      }
      return {
        transform: `translateX(${offsetX - 75}px)`,
      };
    }
    // 不是显示输入框时
    if (edgeState !== "input") {
      return {
        transform: `translateX(${offsetX / 2}px)`,
      };
    }
    return {
      transform: `translateX(${offsetX / 4 - 10}px)`,
    };
  };
  const bottomVariants = {
    init: {
      opacity: 0,
      scale: 0,
    },
    show: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        type: "spring",
        ease: "easeInOut",
      },
    },
    hide: {
      opacity: 0,
      scale: 0,
      transition: {
        duration: 0.5,
        type: "spring",
        ease: "easeOut",
      },
    },
  };

  // stock 返回结果去重
  const stockUnique = arr => {
    if (func.isEmpty(arr)) return;
    const hasObj = {};
    return arr
      .filter(x => x)
      .reduce((total, next) => {
        const filterKey = next?.name + next?.price + next?.symbol;
        if (func.isEmpty(hasObj[filterKey])) {
          hasObj[filterKey] = true;
          total.push(next);
        }
        return total;
      }, []);
  };
  const runGroupNode = async (nodes, queryText, selectValueParam, newAiDescription, lineParam) => {
    const sourceArr = nodes.filter(x => x.parentNode === source) || [];
    let targetArr = nodes.filter(x => x.parentNode === target) || [];
    const sourceLen = sourceArr.length;
    const targetLen = targetArr.length;
    //按Y轴排序
    sourceArr.sort((a, b) => a.position.y - b.position.y);
    targetArr.sort((a, b) => a.position.y - b.position.y);
    const arr = [];
    // 如果runall后源group的node数量增多，目标group需要新增对应数量的node
    if ((selectValueParam || selectValue) !== "showStockInterface") {
      if (sourceLen > targetLen) {
        const lastTargetNode = targetArr[targetLen - 1];
        const newNodes = [];
        for (let index = 0; index < sourceLen - targetLen; index++) {
          const nodeIndex = creatorStoreMethods.getHomeNextNodeIndex();
          const newNode = {
            id: `editor-${nodeIndex}`,
            type: "customNode",
            parentNode: target,
            extent: "parent",
            data: {
              ...lastTargetNode.data,
            },
            position: {
              x: lastTargetNode.position.x,
              y: lastTargetNode.position.y + distance,
            },
          };
          newNodes.push(newNode);
        }
        targetArr = [...targetArr, ...newNodes];
        creatorNodesStore.setNodes(prevList => [...prevList, ...newNodes]);
      }
      // 如果源group的node数量减少，目标group也需要减少node
      else if (sourceLen < targetLen) {
        const newTargetArr = targetArr.slice(0, sourceLen);
        const delTargetArr = targetArr.filter(x => !newTargetArr.some(y => y.id === x.id));
        creatorNodesStore.setNodes(prevList => {
          return prevList.filter(x => !delTargetArr.some(y => y.id === x.id));
        });
      }

      sourceArr.forEach((x, index) => {
        const targetNode = targetArr[index];
        arr.push(
          runApi(queryText, selectValueParam, newAiDescription, x?.data?.textAreaValue, targetNode?.id, x.id, lineParam)
        );
      });
    } else {
      // group的showStockInterface不再调用runapi方法
      // Todo: 还需要处理这种情况下的undo
      sourceArr.forEach(x => {
        arr.push(
          handleStockInterfaceFunc(x.data.textAreaValue, {
            id,
            target,
            source: x.id,
            lineParam,
          })
        );
      });
    }
    // lineParam.isFromGroup = true;

    creatorStoreMethods.setEdgeLineParam({
      id,
      loading: true,
    });
    const resArr = await Promise.allSettled(arr);
    // TODO: Hardcoded
    if ((selectValueParam || selectValue) === "showStockInterface") {
      const resData = resArr
        .filter(x => x.status === "fulfilled")
        .map(x => x.value.formatRes)
        .flat();
      homeServices.shardSplittingAll(
        Object.assign({ lineParam: data.lineParam }, { source, target, id }),
        stockUnique(resData),
        selectValueParam || selectValue
      );
    }
    creatorStoreMethods.setEdgeLineParam({
      id,
      loading: false,
    });
    creatorStoreMethods.onNodeContentChange(
      HomeMethodsUtil.getStartEndNodesContentArr(prevNodeContent.current, undefined, creatorNodesStore.state.nodes)
    );
  };
  const runApi = async (queryText, selectValueParam, newAiDescription, nodeText, target, sourceId, lineParam) => {
    try {
      // const currentSourceNode = nodes.find(x => x.id === source)
      if (disabledRun) {
        return;
      }

      // if (util.isEmpty(currentSourceNode)) return;
      // const nodeText = currentSourceNode.data.textAreaValue
      if (
        feature_tags.enableSkipEmptyVerification &&
        skipEmptyVerification(selectValueParam || data.lineParam.enterText)
      ) {
        // Skip empty verification
      } else if (func.isEmpty(nodeText)) {
        // message.warning('Need input value')
        func.customMsg({
          content: "Need input value",
          type: "warning",
        });
        return;
      }

      const nodes = creatorNodesStore.getNodes();
      const targetNode = nodes.find(x => x.id === target);
      if (func.isEmpty(targetNode)) return;
      prevNodeContent.current.push({
        id: target,
        textAreaValue: targetNode.data.textAreaValue,
        displayType: targetNode.data.displayType,
      });
      const lineParams = Object.assign({}, data.lineParam, { ...lineParam });

      // 请求前重置错误提示
      setNodeErrorTip(target, {}, true);

      // 下拉框选择模式
      if (CustomEdgeUtil.isFunctionInputText(newAiDescription || aiDescription)) {
        // const text = (newAiDescription || aiDescription).replace(FUNCTION_REG, '')
        // const currSelectOption = imageGenSelectOptions.find(x => x.label === text)
        return await homeServices.getOtherTypeData(
          {
            id,
            target,
            source: sourceId,
            lineParam: lineParams,
          },
          selectValueParam || selectValue,
          nodeText
        );
      }

      const lineType = lineParams.lineType;
      // 是聊天框类型
      const isChat = await setChatBoxParam({
        target,
        edgeId: id,
        lineParams,
        setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
      });
      if (isChat) return;

      // If it is Agent type
      if (feature_tags.showStudiosAgent) {
        const isAgent = await setConnectAgentNode({
          selectValueParam: selectValueParam || data.lineParam.enterText,
          nodeText,
          target,
          edgeId: id,
          lineParams,
          setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
        });
        if (isAgent) return;
      }

      // 默认模式
      // if lineType is 'identifier', use '/nlp/completions_generations' otherwise use '/nlp/brain_openai'
      let postParam = {};
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      if (lineType === "identifier" || queryText.type) {
        url = "/stream/bas-demo-v4/nlp/completions_generation";
        // postParam = {
        //   identifier_type: queryText?.type || lineParams?.identifier?.type,
        //   identifier_value: queryText?.value || lineParams?.identifier?.value,
        //   variables: {
        //     input: nodeText,
        //   },
        //   stream: true
        // }

        postParam = await getCompletionsGenerationParam(
          queryText?.type || lineParams?.identifier?.type,
          queryText?.value || lineParams?.identifier?.value,
          {
            input: nodeText,
          }
        );
      } else {
        postParam = getOpenAiParam({
          lineParams,
          prompt: queryText || lineParams.queryValue || "",
          nodeText,
        });
      }

      let temp = "";

      await postWithStreaming(
        url,
        postParam,
        msg => {
          temp += msg.choices?.[0]?.text || msg.choices?.[0]?.delta?.content || "";
          creatorNodesStore.setNodes(prevNodes => {
            return prevNodes.map(l => {
              if (l.id === target) {
                const displayType = getAutomaticDisplayType(l.data, temp);
                return {
                  ...l,
                  data: {
                    ...l.data,
                    showWarning: false,
                    displayType: displayType,
                    textAreaValue: temp.trim(),
                  },
                };
              }
              return l;
            });
          });
        },
        false,
        postInstance => {
          creatorStoreMethods.setNodeParam({
            id: target,
            data: {
              showStopGenerateBtn: true,
              abortNode: {
                source,
                target,
                edgeId: id,
                apiInstance: postInstance,
              },
            },
          });
        }
      );
    } catch (error) {
      // console.error('run api', error);
      // util.customMsg('Run failed.')
      setNodeErrorTip(target, error);
      if (!error.ok && error.response) {
        logEvent("http", {
          function_name: selectValueParam || "prompt",
          url: error.response.url,
          status_code: error.response.status,
        });
      }
    } finally {
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          showStopGenerateBtn: false,
          showFeedback: true,
          abortNode: {},
        },
      });
    }
  };

  //TODO: move processing code to event.js
  const handleLogEvent = (id, isManualClick, isPrompt, selectValueParam, label, nodeText) => {
    const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;
    let eventProperties = {};
    let type = "";
    let prompt = "";
    let function_name = "";
    try {
      if (isPrompt) {
        //prompt
        type = "prompt";
        prompt = selectValueParam || label;
      } else {
        //functions
        let selectedOption = {};
        if (isManualClick) {
          selectedOption = imageGenSelectOptionsArr.find(x => x.label === label);
        } else {
          selectedOption = imageGenSelectOptionsArr.find(x => x.value == selectValueParam);
        }
        type = selectedOption?.groupTitle || "other";
        function_name = selectedOption?.label || "";
      }
      eventProperties = {
        input: nodeText,
        prompt: prompt,
        function_name: function_name,
        isManualClick: isManualClick,
        type: type,
        prompt_id: data.lineParam?.identifier?.value || "",
        edge_id: id,
      };
      logEvent("run_edge", eventProperties);
    } catch (error) {
      console.error("err::", error);
    }
  };

  // 第一次运行边时显示 Publish 提示框
  const showPublishPrompt = () => {
    // 获取最新的selectedTemplate value
    const selectedTemplate = store.getState().studio.selectedTemplate;
    // 项目已经显示过，不在显示提示框。
    // 进入引导页，不显示提示框。
    if (func.isEmpty(selectedTemplate?.v2?.showToolTipStatus) || selectedTemplate?.v2?.showToolTipStatus === "close")
      return;
    const v2 = Object.assign({}, selectedTemplate.v2, { showToolTipStatus: "show" });
    const data = Object.assign({}, selectedTemplate, { v2 });
    setSelectedTemplate(data);
  };
  const handleStopRun = () => {
    const nodes = creatorNodesStore.getNodes();
    const currNode = nodes.find(x => x.id === target);
    // 处理单个node/group中保存apiInstance情况
    if (currNode && !func.isEmpty(currNode.data.abortNode?.apiInstance)) {
      if (Array.isArray(currNode.data.abortNode.apiInstance)) {
        currNode.data.abortNode.apiInstance.forEach(x => x?.cancel());
      } else {
        currNode.data.abortNode.apiInstance?.cancel();
      }
      return;
    }
    // 处理group中的node保存apiInstance情况
    if (GROUPID_REG.test(currNode.id)) {
      const childNodes = nodes.filter(x => x.parentNode === currNode.id);
      childNodes.forEach(x => x.data?.abortNode?.apiInstance?.cancel());
    }
  };
  const clickRunBtn = async (param = {}) => {
    try {
      setShowTooltip("close");
      if (data.lineParam.loading) {
        handleStopRun();
        return;
      }
      const label = data.lineParam.enterText.replace("/", "");
      const nodes = creatorNodesStore.getNodes();
      // const edges = creatorEdgesStore.getEdges()
      const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;

      let { event = null, queryText = "", selectValueParam = "", newAiDescription = "", lineParam = {} } = param;

      if (!func.isEmpty(event)) {
        event.preventDefault();
        event.stopPropagation();
      }
      // 处理上传文件
      if (await handleUploadFileRun(newAiDescription || label, source, target, id, disabledRun)) return;
      // 处理切分
      if (
        GROUPID_REG.test(target) &&
        !func.isEmpty(newAiDescription) &&
        newAiDescription !== "/Split Output" &&
        data.lineParam.enterText === "/Split Output"
      ) {
        homeServices.groupChanged(id);
        return;
      }

      const isManualClick = selectValueParam == "";

      // 手动点击clickRunBtn没传参数，这里重新赋值，使得自定义func不调用出错
      const selectOption = imageGenSelectOptionsArr.find(x => x.label === label && !x.isCreatedManual);
      if (
        func.isEmpty(selectOption) &&
        func.isEmpty(selectValueParam) &&
        CustomEdgeUtil.isFunctionInputText(data.lineParam.enterText) &&
        label !== "Stock API"
      ) {
        selectValueParam = label;
        newAiDescription = data.lineParam.enterText;
      }
      // setHideEditInput(false)

      prevNodeContent.current = [];
      // 处理分组
      if (GROUPID_REG.test(source)) {
        await runGroupNode(nodes, queryText, selectValueParam, newAiDescription, lineParam);
        return;
      }

      const isPrompt = newAiDescription == "" && func.isEmpty(selectOption);

      const currentSourceNode = nodes.find(x => x.id === source);
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          loadingNodeHtml: false,
        },
      });
      if (func.isEmpty(currentSourceNode)) return;

      const nodeText = utilIndex.getTextFromTextAreaValueByType(currentSourceNode.data);
      creatorStoreMethods.setEdgeLineParam({
        id,
        loading: true,
      });
      const disableNextEdgeArr = disableNextEdges();

      await runApi(queryText, selectValueParam, newAiDescription, nodeText, target, source, lineParam);
      // 处理目标节点下是否有切分group
      const edges = creatorEdgesStore.getEdges();
      // 定向清除 run disable
      const closeRunDisable = store.getState().editor.needDisableEdgeIds.filter(x => !disableNextEdgeArr.includes(x));
      setNeedDisableEdgeIds([...closeRunDisable]);

      const groupArr = edges.filter(x => x.source === target && GROUPID_REG.test(x.target));
      if (!func.isEmpty(groupArr)) {
        groupArr.forEach(x => {
          const currentNodes = data.nodes.current;
          const groupId = x.target;
          const changeNodes = currentNodes.filter(x => x.id === groupId || x.parentNode === groupId);
          prevNodeContent.current = [
            ...prevNodeContent.current,
            ...changeNodes.map(x => ({
              id: x.id,
              textAreaValue: x.data.textAreaValue,
              displayType: x.data.displayType,
            })),
          ];
        });
      }
      creatorStoreMethods.onNodeContentChange(
        HomeMethodsUtil.getStartEndNodesContentArr(prevNodeContent.current, undefined, creatorNodesStore.state.nodes)
      );

      // 判断只有一个node添加边自动生成input field
      if (nodes.length === 2 && edges.length === 1) {
        homeHandleFieldMethods.autoSetInputField();
      }
      handleLogEvent(id, isManualClick, isPrompt, selectValueParam, label, nodeText);
    } catch (error) {
      console.error("err::", error);
    } finally {
      creatorStoreMethods.setEdgeLineParam({
        id,
        loading: false,
      });
      showSecendTooltip();
      // 判断是否显示publish prompt
      showPublishPrompt();
    }
  };

  const childSetEdgeLineParam = params => {
    params.id = id;
    creatorStoreMethods.setEdgeLineParam(params);
  };
  function getFilterSelectOptions(text) {
    const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;
    const textFilter = text.replace(/^\//, "");
    return imageGenSelectOptionsArr.filter(x =>
      x.label?.toLocaleLowerCase()?.includes(textFilter?.toLocaleLowerCase())
    );
  }

  const doubleClickLine = e => {
    if (data.lineParam.loading) return;
    e.preventDefault();
    e.stopPropagation();

    prevEdgeData.current = Object.assign(
      {},
      {
        ...data,
        lineParam: {
          ...data.lineParam,
        },
      }
    );
    prevAiDescription.current = aiDescription;
    if (CustomEdgeUtil.isFunctionInputText(aiDescription)) {
      creatorEdgesStore.setQueryLoading(id, "isGetQuery", false);
      const apiArrs = getFilterSelectOptions(searchFunc);
      setApiArr(apiArrs);
      setShowImageGenSelect(true);
      return;
    }
    creatorEdgesStore.setQueryLoading(id, "isGetQuery", false);
    setTimeout(() => {
      const aiInput = aiDescriptionInputRef.current;
      if (aiInput) {
        aiInput.focus();
        const textArea = get(aiInput, "resizableTextArea.textArea");
        const valueLength = get(textArea, "value.length", 0);
        textArea?.setSelectionRange(valueLength, valueLength);
        resetAiDescriptionWidth();
      }
    }, 0);
  };

  const setCloseModal = e => {
    setIsModelOpen(false);
  };
  const createCustomJSFunction = () => {
    // const nodes = creatorNodesStore.getNodes()
    // const currentSourceNode = nodes.find(x => x.id === source)
    // setCurrentSourceNodeValue(currentSourceNode.data.textAreaValue)
    // setCreateFunction(true)
    setCreateJSFuncData({
      open: true,
      sourceNodeId: source,
      targetNodeId: target,
      state: "new",
      edgeId: id,
      parameter: {
        name: "No function name",
        description: "",
      },
    });
  };
  const createAPIFunction = () => {
    navigate("function/Untitled");
    setCreateAPIFuncData({
      open: true,
      sourceNodeId: source,
      targetNodeId: target,
      state: "new",
      edgeId: id,
      parameter: {
        name: "No function name",
        description: "",
      },
    });
  };
  const clickCreateFunction = item => {
    const obj = {
      createCustomJSFunc: createCustomJSFunction,
      createAPIFunc: createAPIFunction,
    };

    obj[item.value]?.();
  };
  // const onCloseCustomModel = () => {
  //   setCreateFunction(false)
  // }
  const requestSavedFunction = async name => {
    const apiArrs = getFilterSelectOptions("");
    setApiArr(apiArrs);
    const currentOption = apiArrs.find(x => x.value === name);
    clickToSelectValue(currentOption);
    setCreateFunctionClick({});
  };

  const closeCustomDropdown = e => {
    creatorEdgesStore.setQueryLoading(id, "isGetQuery", true);
    // setHideEditInput(false)
    setShowImageGenSelect(false, e?.target);
  };

  const outClickBindObj = {
    deleteEdge() {
      setTimeout(() => {
        if (document.activeElement.nodeName !== "BODY") {
          return;
        }

        const edges = creatorEdgesStore.getEdges();
        const latestEdge = edges[edges.length - 1];

        if (latestEdge.id === id) {
          deleteNodeById(latestEdge.target);
        }
      });
    },

    onOutClick: e => {
      // // 排除右击输入框的情况
      // if (e.type !== 'contextmenu') {
      // 当点击除 customDrodow 时，包含边上的输入框，其余情况都关闭
      const textAreaDom = aiDescriptionInputRef.current?.resizableTextArea?.textArea;

      // 有内容 关闭 function 框
      if (!func.isEmpty(textAreaDom?.value)) {
        // if (searchFuncRef.current) {
        //   setAiDescription(`/${searchFuncRef.current}`)
        // }

        setShowImageGenSelect(false, e.target);
        return;
      }

      if (textAreaDom && textAreaDom === document.activeElement) {
        return;
      }

      outClickBindObj.handleCloseCustomDrop(e);
    },

    handleCloseCustomDrop: e => {
      // 记录是否是第一次运行 run
      const isFirstRun = !prevAiDescription.current;
      // 1. 第一次点击
      // 1.1 有无内容 直接关闭 并删除节点
      if (isFirstRun) {
        outClickBindObj.deleteEdge();
        // 删除边时聚焦前一个 node
        // focusNodeById(source)
        return;
      }

      // 2. 第二次点击
      // 2.1 与之前内容一样 关闭
      // 2.2 与之前内容不一样 还原之前的内容 关闭
      const prevEnterText = data.lineParam.enterText;
      const trimAiDescription = aiDescription.trim();
      if (prevEnterText === trimAiDescription) {
        closeCustomDropdown(e);
      } else {
        setAiDescription(prevEnterText);
        closeCustomDropdown(e);
      }
    },
  };

  const handleInputBlur = () => {
    // 如果下拉框已经打开 关闭的动作交给 outClick
    if (showImageGenSelect) {
      return;
    }

    // input 有值不删除 node
    if (func.isEmpty(aiDescription)) {
      outClickBindObj.handleCloseCustomDrop();
    }
  };

  // const handleCommandInputBlur = () => {
  //   setTimeout(() => {
  //     if (
  //       document.activeElement !== aiDescriptionInputRef.current?.resizableTextArea?.textArea
  //       && showImageGenSelect
  //       && !util.isEmpty(data.lineParam.enterText)
  //     ) {
  //       setShowImageGenSelect(false)
  //       creatorEdgesStore.setQueryLoading(id, 'isGetQuery', true);
  //     }
  //   }, 80);
  // }
  const onAiDescriptionChange = e => {
    if (data.lineParam.loading) return;
    const text = e.target.value;
    const trimText = text.trim();

    // 输入为空重置下拉框
    if (func.isEmpty(trimText)) {
      setShowImageGenSelect(false);
    }

    if ((func.isEmpty(trimText) || trimText === "/") && !func.isEmpty(searchFunc)) {
      setSearchFunc("");
    }
    // 下拉框选择模式
    if (CustomEdgeUtil.isFunctionInputText(trimText)) {
      let apiArrs = getFilterSelectOptions(trimText);
      const sourceNodeType = sourceNode?.data?.displayType;
      if (!ENABLE_SPLIT_TYPES.includes(sourceNodeType)) {
        apiArrs = apiArrs.filter(x => x.value !== "splitOutput");
      }

      setApiArr(apiArrs);

      setShowImageGenSelect(true);
      // // 给输入框赋初始下拉选项值
      // setAiDescription(`/${imageGenSelectOptions[0].label}`)
    } else {
      // 默认模式
      // TODO 仅仅在点击外部时关闭下拉框
      // if (showImageGenSelect) {
      //   setShowImageGenSelect(false)
      // }
    }
    setAiDescription(text);
    // setHideEditInput(trimText !== prevAiDescription.current.trim())
  };

  const onEdgeInputFocus = e => {
    // 将 aiDescription 值拆分
    // if (CustomEdgeUtil.isFunctionInputText(aiDescription)) {
    //   const [edgeText, commandText] = CustomEdgeUtil.parseEdgeAndCommandbarText(aiDescription)
    //   setAiDescription(edgeText)
    //   setSearchFunc(commandText)
    //   if (commandText) {
    //     setShowImageGenSelect(true)
    //   }
    // }

    onFocusToFocusEdge();
  };

  function handleSetPrevLineParamStr(param) {
    const newLineParam = Object.assign({}, data.lineParam, {
      ...param,
    });
    const newLineParamStr = JSON.stringify(newLineParam);
    const prevLineParamStr = JSON.stringify(prevEdgeData.current.lineParam || {});
    if (prevLineParamStr !== newLineParamStr) {
      homeChildContainer.onEdgeContentChange([
        {
          id,
          startEdgeData: {
            isGetQuery: prevEdgeData.current?.isGetQuery || false,
            lineParamStr: prevLineParamStr,
          },
          endEdgeData: {
            isGetQuery: true,
            lineParamStr: newLineParamStr,
          },
        },
      ]);
    }
  }

  // const getFunctionMapping = async (text) => {
  //   try {
  //     setEdgeState('generating')
  //     // 使用文本查询输入内容类型
  //     const res = await postData('/be/cot/function/mapping', {description: text})
  //     const functionType = res?.data?.function_type || ''

  //     const functionNameFromAlias = getFunctionNameByAlias(functionType);
  //     const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions
  //     const functionMappingData = imageGenSelectOptionsArr.find(x => x.label === functionNameFromAlias)
  //     // 匹配已有 function ? 返回定义function : 默认为空
  //     return !isEmpty(functionMappingData) ? functionMappingData : null;
  //   } catch (error) {
  //     console.error('Get domain mapping failed:', error)
  //     return ''
  //   }
  // }

  const showSecendTooltip = () => {
    if (getShowTooltip === "edge-0") {
      setCloseToolTipArr(["edge-0"]);
      const nodesArr = creatorNodesStore.getNodes();
      const nodeStr = nodesArr.some(x => x.id === "group-2") ? "group-2" : "editor-2";
      setShowTooltip(nodeStr);
    }
  };

  const onPressEnter = async e => {
    setShowTooltip("close");
    setShowImageGenSelect(false);
    let text = e.target.value;
    if (await handleUploadFileRun(text, source, target, id, disabledRun)) {
      //  如果是uploadTypes类型的source，output node也需要处理tooltip
      showSecendTooltip();
      return;
    }
    // const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions
    // 下拉框选择模式
    if (CustomEdgeUtil.isFunctionInputText(aiDescription)) {
      const currentOption =
        !func.isEmpty(apiArr) && apiArr.length === 1
          ? apiArr[0]
          : imageGenSelectOptions.find(x => x.value === selectValue);

      clickToSelectValue(currentOption);
      return;
    }
    // 默认模式
    if (func.isEmpty(text)) {
      func.customMsg({
        content: "Need input value",
        type: "warning",
      });
      return;
    }

    const { identifier, lineType } = (await homePlugin.getAndSetQuery(id, text)) || {};
    data.lineParam.identifier = identifier;

    handleSetPrevLineParamStr({
      enterText: text,
      lineType,
      identifier,
    });
    // const queryText = null
    if (!identifier) {
      setAiDescription("");
      return;
    }

    // 如果左边node有内容，输入AI描述回车后应该自动run
    const currentSourceNode = data.nodes.current.find(x => x.id === source);
    const enableSkipEmptyVerification = feature_tags.enableSkipEmptyVerification && skipEmptyVerification(text);

    if (
      enableSkipEmptyVerification ||
      !func.isEmpty(currentSourceNode.data.textAreaValue) ||
      GROUPID_REG.test(source)
    ) {
      await clickRunBtn({
        lineParam: {
          identifier,
          lineType,
        },
        selectValueParam: text,
      });
    }
  };

  const clickToSelectValue = async j => {
    const newAiDescription = `/${j.label}`;
    logEvent("click_function", { function_name: j.value });
    handleSetPrevLineParamStr({
      enterText: newAiDescription,
    });

    setAiDescription(newAiDescription);
    setSelectValue(j.value);
    // 隐藏下拉选项
    setShowImageGenSelect(false);
    // 给按钮下的文本赋值
    creatorStoreMethods.setEdgeLineParam({
      id,
      enterText: newAiDescription,
    });
    // 显示run all按钮
    creatorEdgesStore.setQueryLoading(id, "isGetQuery", true);
    // setHideEditInput(false)
    if (disableAutoRunList.includes(j.value)) return;
    await clickRunBtn({
      selectValueParam: j.value,
      newAiDescription,
    });
    setSearchFunc("");
  };
  const handleValueChange = value => {
    const apiArrs = getFilterSelectOptions(value);
    setApiArr(apiArrs);
    setSearchFunc(value);
  };
  const onCommandKeyDown = e => {
    if (e.keyCode === 8 && func.isEmpty(searchFunc)) {
      setShowImageGenSelect(false, aiDescriptionInputRef.current);
    }
  };

  const getEdgePathStyle = () => {
    if (checkEdgeArr.includes(id)) {
      return selectedEdgeStyl;
    }
    return style;
  };
  const getCustomEdgeClass = () => {
    const classes = [
      `${edgeStyle["custom-edge-box"]} nodrag nopan `,
      `${(isModelOpen && lineParam.id === id) || showImageGenSelect ? "model-location" : ""}`,
    ];
    return classes.join(" ");
  };
  // const onAnimationComplete = () => {
  //   // reactflow版本>=11.7.1, allow array of ids as updateNodeInternals arg
  //   creatorNodesStore.getNodes().forEach(x => {
  //     updateNodeInternals(x.id)
  //   })
  // }
  return (
    <>
      <motion.path
        id={id}
        style={getEdgePathStyle()}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        onDoubleClick={doubleClickLine}
        variants={pathVariants}
        initial={"hidden"}
        animate={"visible"}
        // onAnimationComplete={onAnimationComplete}
      />
      {!data.isCreatedBySlice && sameTargetEdgeIds.includes(id) ? (
        <EdgeLabelRenderer>
          <TooltipWhatTodo
            id={id}
            type={"edge"}
            show={introTooltipObj.show}
            title={introTooltipObj.title}
            content={introTooltipObj.content}
            classNameSuffix={introTooltipObj.classNameSuffix}
            className={edgeStyle.edgeTooltip}
            overlayStyle={getOverlayStyl()}
          >
            <div
              style={getRenderBoxStyl()}
              className={getCustomEdgeClass()}
              data-testid={id}
              // onKeyDown={onSelectKeyDown}
            >
              {/* <p style={{ position: 'absolute', top:'-80px'}}>{id}</p> */}
              {/* <p style={{ position: 'absolute', top:'-80px'}}>{labelX}&nbsp;{labelY}&nbsp;{offsetX}&nbsp;{offsetY}</p> */}
              {/* AI description输入框 */}
              {edgeState === "input" ? (
                <>
                  <span className={edgeStyle["ai-text-span"]} ref={aiDescriptionSpanRef}>
                    {aiDescription}
                  </span>

                  <div className={edgeStyle["ai-textArea-wrap"]}>
                    <CommandBar.Dropdown
                      open={showImageGenSelect}
                      overlay={
                        <CommandBar.CommandPanel
                          apiArr={apiArr}
                          sourceId={source}
                          aiDescription={aiDescription}
                          disableHover={disableHover}
                          searchFunc={searchFunc}
                          aiDescriptionInputRef={aiDescriptionInputRef}
                          onCommandKeyDown={onCommandKeyDown}
                          handleValueChange={handleValueChange}
                          clickToSelectValue={clickToSelectValue}
                          clickCreateFunction={clickCreateFunction}
                          onHideDropdown={outClickBindObj.onOutClick}
                        />
                      }
                    >
                      <TextArea
                        className={`nowheel ${edgeStyle["ai-textArea"]}`}
                        {...sharedProps}
                        style={{
                          width: aiDescriptionWidth + "px",
                        }}
                        autoSize={{ minRows: 1, maxRows: 9 }}
                        value={aiDescription}
                        onChange={onAiDescriptionChange}
                        onPressEnter={onPressEnter}
                        onBlur={handleInputBlur}
                        placeholder={AI_INPUT_PLACEHOLDER}
                        onFocus={onEdgeInputFocus}
                      />
                    </CommandBar.Dropdown>
                  </div>
                </>
              ) : null}

              {/* loading */}
              {edgeState === "generating" ? <Spin /> : null}

              {/* run按钮及弹窗 */}
              {edgeState === "run" ? (
                <>
                  {/* <AnimatePresence>
                        {
                          isModelOpen && lineParam.id === id ?
                            <motion.div
                              className={edgeStyle['model-box']}
                              variants={bottomVariants}
                              initial="init"
                              animate="show"
                              exit="hide">
                              <CodeModal
                                setCloseModal={setCloseModal}
                              />
                            </motion.div>
                            : null
                        }
                      </AnimatePresence> */}
                  <div className={edgeStyle["line-button-box"]}>
                    <Button
                      data-testid="EdgeRunButton"
                      className={edgeStyle["flow-line-button"]}
                      onClick={() => clickRunBtn()} //这里传空值
                      type="primary"
                      shape="round"
                      disabled={disabledRun || needDisableEdgeIds.includes(id)}
                    >
                      {data.lineParam.loading ? (
                        <>
                          <LoadingOutlined
                            style={{
                              marginRight: "5px",
                              verticalAlign: "baseline",
                            }}
                            spin
                          />
                          <span>Stop</span>
                        </>
                      ) : (
                        <span>Run</span>
                      )}
                    </Button>
                    <p
                      style={{
                        width: aiDescriptionWidth + "px",
                      }}
                      className={`${edgeStyle["flow-line-p"]} ${
                        !func.isEmpty(selectIdArr) ? edgeStyle["not-optional"] : ""
                      }`}
                      onDoubleClick={doubleClickLine}
                    >
                      {data.lineParam.enterText}
                    </p>
                  </div>
                  {data.lineParam.enterText === "/Add Data Source" ? (
                    <SourceOfTruth setEdgeLineParam={childSetEdgeLineParam} lineParam={data.lineParam} />
                  ) : null}
                </>
              ) : null}
            </div>
          </TooltipWhatTodo>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

export default function CustomEdge(props) {
  const _props = { ...props, isV3: false };
  return (
    <CustomEedgProvider initialState={_props}>
      <CustomEdgeInner {..._props} />
    </CustomEedgProvider>
  );
}
